import { useMemo, useState, useEffect } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { GET_PRODUCTS } from '../graphql/product-queries'
import { UPDATE_PRODUCT } from '../graphql/product-mutations'
import { Product } from '../types/product'
import {
  ALLERGENS,
  ATTRIBUTES,
  allergenLabel,
  allergenEmoji,
  attributeLabel,
  attributeColor,
} from '../lib/allergen-attribute-config'
import AllergenAttributeEditor, { AllergenAttributeValue } from '../components/AllergenAttributeEditor'
import { usePermissions } from '../hooks/usePermissions'
import { PERMISSIONS } from '../lib/permissions'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'

// A product is "not reviewed" when neither JSON blob has ever been set (both null),
// as opposed to being deliberately cleared (allergens: []).
const isNotReviewed = (p: Product) =>
  (p.allergen_info_json === null || p.allergen_info_json === undefined) &&
  (p.attributes_json === null || p.attributes_json === undefined)

const allergensOf = (p: Product) => p.allergen_info_json?.allergens ?? []
const attributesOf = (p: Product) => p.attributes_json?.attributes ?? []

const AllergenOverviewPage = () => {
  const { hasPermission } = usePermissions()
  const canEdit = hasPermission(PERMISSIONS.PRODUCT_UPDATE)

  const { data, loading, error } = useQuery(GET_PRODUCTS, { variables: { filters: {} } })
  const products: Product[] = data?.products ?? []

  // --- Filters ------------------------------------------------------------
  const [search, setSearch] = useState('')
  const [categoryId, setCategoryId] = useState<number | 'all'>('all')
  const [codeFilter, setCodeFilter] = useState<string | null>(null)
  const [notReviewedOnly, setNotReviewedOnly] = useState(false)

  const categories = useMemo(() => {
    const map = new Map<number, string>()
    products.forEach((p) => (p.categories ?? []).forEach((c) => map.set(c.id, c.name)))
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name))
  }, [products])

  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false
      if (categoryId !== 'all' && !(p.categories ?? []).some((c) => c.id === categoryId)) return false
      if (notReviewedOnly && !isNotReviewed(p)) return false
      if (codeFilter && !allergensOf(p).includes(codeFilter) && !attributesOf(p).includes(codeFilter)) {
        return false
      }
      return true
    })
  }, [products, search, categoryId, codeFilter, notReviewedOnly])

  const notReviewedCount = useMemo(() => products.filter(isNotReviewed).length, [products])

  // --- Quick-edit drawer --------------------------------------------------
  const [editing, setEditing] = useState<Product | null>(null)
  const [draft, setDraft] = useState<AllergenAttributeValue>({ allergens: [], attributes: [] })

  useEffect(() => {
    if (editing) {
      setDraft({ allergens: allergensOf(editing), attributes: attributesOf(editing) })
    }
  }, [editing])

  const [updateProduct, { loading: saving }] = useMutation(UPDATE_PRODUCT, {
    onCompleted: () => {
      toast.success('Product updated')
      setEditing(null)
    },
    onError: (err) => toast.error(err.message || 'Failed to update product'),
  })

  const handleSave = () => {
    if (!editing) return
    updateProduct({
      variables: {
        id: editing.id,
        productData: {
          allergen_info_json: { allergens: draft.allergens },
          attributes_json: { attributes: draft.attributes },
        },
      },
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">❌</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Couldn't load products</h3>
        <p className="text-gray-500">{error.message}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Allergens & Attributes</h1>
        <p className="text-sm text-gray-500">
          Audit which products have allergen and attribute metadata set for the kiosk and webshop.
        </p>
      </div>

      {/* Summary */}
      {notReviewedCount > 0 && (
        <div className="flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
          <span>
            <strong>{notReviewedCount}</strong> of {products.length} products have no allergen or
            attribute data set yet.
          </span>
          {!notReviewedOnly && (
            <button
              onClick={() => setNotReviewedOnly(true)}
              className="ml-auto text-amber-900 underline hover:no-underline"
            >
              Show only these
            </button>
          )}
        </div>
      )}

      {/* Filter bar */}
      <div className="card space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products…"
            className="flex-1 min-w-[12rem] border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={notReviewedOnly}
              onChange={(e) => setNotReviewedOnly(e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            Not reviewed only
          </label>
        </div>

        {/* Code filter chips */}
        <div>
          <p className="text-xs font-medium text-gray-500 mb-2">
            Filter by a specific allergen or attribute {codeFilter && '— showing products that have it'}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {codeFilter && (
              <button
                onClick={() => setCodeFilter(null)}
                className="inline-flex items-center rounded-full bg-gray-800 px-2.5 py-0.5 text-xs font-medium text-white"
              >
                Clear filter <XMarkIcon className="ml-1 h-3 w-3" />
              </button>
            )}
            {ATTRIBUTES.map((code) => (
              <button
                key={code}
                onClick={() => setCodeFilter(codeFilter === code ? null : code)}
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border ${
                  codeFilter === code
                    ? `${attributeColor(code)} border-transparent ring-2 ring-primary-400`
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {attributeLabel(code)}
              </button>
            ))}
            {ALLERGENS.map((code) => (
              <button
                key={code}
                onClick={() => setCodeFilter(codeFilter === code ? null : code)}
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border ${
                  codeFilter === code
                    ? 'bg-gray-800 text-white border-transparent ring-2 ring-primary-400'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {allergenEmoji(code)} {allergenLabel(code)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-x-auto">
        <p className="text-sm text-gray-500 mb-3">
          {filtered.length} product{filtered.length !== 1 ? 's' : ''}
        </p>
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead>
            <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <th className="px-3 py-2">Product</th>
              <th className="px-3 py-2">Category</th>
              <th className="px-3 py-2">Attributes</th>
              <th className="px-3 py-2">Allergens</th>
              <th className="px-3 py-2">Status</th>
              {canEdit && <th className="px-3 py-2" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map((p) => {
              const attrs = attributesOf(p)
              const allergens = allergensOf(p)
              const notReviewed = isNotReviewed(p)
              return (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">{p.name}</td>
                  <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                    {(p.categories ?? []).map((c) => c.name).join(', ') || '—'}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {attrs.length === 0 ? (
                        <span className="text-gray-400">—</span>
                      ) : (
                        attrs.map((code) => (
                          <span
                            key={code}
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${attributeColor(code)}`}
                          >
                            {attributeLabel(code)}
                          </span>
                        ))
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {allergens.length === 0 ? (
                        <span className="text-gray-400">—</span>
                      ) : (
                        allergens.map((code) => (
                          <span
                            key={code}
                            className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700"
                            title={allergenLabel(code)}
                          >
                            {allergenEmoji(code)} {allergenLabel(code)}
                          </span>
                        ))
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {notReviewed ? (
                      <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                        Not reviewed
                      </span>
                    ) : allergens.length === 0 ? (
                      <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                        No allergens
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                        Set
                      </span>
                    )}
                  </td>
                  {canEdit && (
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        onClick={() => setEditing(p)}
                        className="text-sm text-primary-600 hover:text-primary-900"
                      >
                        Edit
                      </button>
                    </td>
                  )}
                </tr>
              )
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={canEdit ? 6 : 5} className="px-3 py-8 text-center text-gray-500">
                  No products match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Quick-edit drawer */}
      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-gray-600 bg-opacity-50" onClick={() => setEditing(null)} />
          <div className="relative w-full max-w-lg bg-white shadow-xl h-full overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium text-gray-900">{editing.name}</h2>
                <p className="text-sm text-gray-500">Allergens & attributes</p>
              </div>
              <button onClick={() => setEditing(null)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="px-6 py-4">
              <AllergenAttributeEditor value={draft} onChange={setDraft} />
            </div>
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="btn-secondary">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving} className="btn-primary">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AllergenOverviewPage
